import { supabase, isConfigured } from './supabase';
import type { PipelineStage } from '../types';

export async function getPipelineStages(teamId: string): Promise<PipelineStage[]> {
  if (!isConfigured) return [];

  const { data, error } = await supabase
    .from('sales_pipeline_stages')
    .select('*')
    .eq('team_id', teamId)
    .order('order_index', { ascending: true });

  if (error) {
    console.error('[pipeline] fetch failed:', error);
    return [];
  }

  if (!data || data.length === 0) {
    await supabase.rpc('seed_sales_pipeline_stages', { p_team_id: teamId });
    const { data: seeded } = await supabase
      .from('sales_pipeline_stages')
      .select('*')
      .eq('team_id', teamId)
      .order('order_index', { ascending: true });
    return (seeded || []) as PipelineStage[];
  }

  return data as PipelineStage[];
}

export async function createPipelineStage(
  teamId: string,
  key: string,
  label: string,
  orderIndex: number,
  isWon = false,
  isLost = false
): Promise<PipelineStage | null> {
  if (!isConfigured) return null;

  const { data, error } = await supabase
    .from('sales_pipeline_stages')
    .insert({
      team_id: teamId,
      key,
      label,
      order_index: orderIndex,
      is_won: isWon,
      is_lost: isLost,
    })
    .select()
    .single();

  if (error) {
    console.error('[pipeline] create failed:', error);
    return null;
  }

  return data as PipelineStage;
}

export async function updatePipelineStage(
  id: string,
  updates: Partial<Pick<PipelineStage, 'label' | 'order_index' | 'is_won' | 'is_lost'>>
): Promise<PipelineStage | null> {
  if (!isConfigured) return null;

  const { data, error } = await supabase
    .from('sales_pipeline_stages')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('[pipeline] update failed:', error);
    return null;
  }

  return data as PipelineStage;
}

export async function deletePipelineStage(id: string): Promise<boolean> {
  if (!isConfigured) return false;

  const { error } = await supabase
    .from('sales_pipeline_stages')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('[pipeline] delete failed:', error);
    return false;
  }

  return true;
}

export async function reorderPipelineStages(stages: { id: string; order_index: number }[]): Promise<boolean> {
  if (!isConfigured) return false;

  for (const stage of stages) {
    const { error } = await supabase
      .from('sales_pipeline_stages')
      .update({ order_index: stage.order_index })
      .eq('id', stage.id);

    if (error) {
      console.error('[pipeline] reorder failed:', error);
      return false;
    }
  }

  return true;
}
